import { normalizeEndpoint } from '@/lib/normalize-endpoint';
import type { ApiResponse } from '@/types/api.type';
import type { ExportFromApiOpts, FetchAllOpts, PullOpts } from './types';
import { toFilters } from './filters';
import { save } from './save';
import api from '@/lib/api';

export const fetchAllForExport = async <T>({
  entitySlug,
  endpoint,
  method = 'POST',
  params = {},
  body = {},
  filters,
  pageSize = 200
}: FetchAllOpts): Promise<T[]> => {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const rows: T[] = [];
  let page = 1;
  let totalCount = 0;

  const requestBody: Record<string, any> = { ...body };
  const dynamicFilters = toFilters(filters);

  if (dynamicFilters) {
    requestBody.dynamic_filters = dynamicFilters;
  }

  while (true) {
    const requestParams = {
      ...params,
      page,
      page_size: pageSize
    };

    const response =
      method === 'GET'
        ? await api.get<ApiResponse<T> | T[]>(`/entities/${entitySlug}/erp/${normalizedEndpoint}`, {
            params: requestParams
          })
        : await api.post<ApiResponse<T> | T[]>(`/entities/${entitySlug}/erp/${normalizedEndpoint}`, requestBody, {
            params: requestParams
          });

    const responseData = response.data;
    const pageResults = Array.isArray(responseData) ? responseData : responseData?.results || [];
    totalCount = Array.isArray(responseData) ? totalCount : (responseData?.count ?? totalCount);
    rows.push(...pageResults);

    if (!pageResults.length) {
      break;
    }

    if (!Array.isArray(responseData) && 'next' in responseData) {
      if (!responseData.next) break;
    } else if (pageResults.length < pageSize || (totalCount && rows.length >= totalCount)) {
      break;
    }

    page += 1;
  }

  return rows;
};

export const exportFromApi = async <T>({
  file,
  numFmt,
  skipEmpty,
  ...fetchOptions
}: ExportFromApiOpts<T>): Promise<T[]> => {
  const rows = await fetchAllForExport<T>(fetchOptions);
  if (skipEmpty && rows.length === 0) {
    return rows;
  }

  await save(rows, file, { numFmt });
  return rows;
};

export const pull = async <T>({
  entitySlug,
  endpoint,
  params = {},
  filters,
  pageSize = 200,
  file,
  numFmt
}: PullOpts<T>) => {
  await exportFromApi<T>({
    entitySlug,
    endpoint,
    body: params,
    filters,
    pageSize,
    file,
    numFmt
  });
};
